/**
 * THE GAME'S SCROLLBAR — two drawn pieces, one component, everywhere something scrolls.
 *
 * The track and the capsule are SEPARATE pictures, each a vertical 3-slice: a drawn cap at each end and a middle
 * that repeats. That is what lets the track fill any height and the capsule take any length without either of
 * them being stretched out of shape. The pieces are ordered art (art/src/scroll-2026-09-26) and the slice numbers
 * live in app.css with every other border-image in this game.
 *
 * It replaced a single file that had the capsule baked into the track, which the code could only hide by pushing
 * the picture up by the capsule's length — so the track ran out at the bottom, and the cut carried the scene's
 * own pixels along its edges (owner 26.9.2026: "siinä on liukupalkki pysyvästi osana ja muutoinkin tosi huono
 * laatuinen … Tämä pitää tehdä premium laatuiseksi").
 *
 * Nothing here decides how a scrollbar looks. The only thing read at runtime is the scroll position of the
 * element it belongs to, which no stylesheet can know: that is not laying out the interface (OD-036), and
 * var/tools/no-runtime-layout.mjs records it as a scrollbar.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';

/** the capsule never gets shorter than this, however long the list is — below it there is nothing left to grab */
const MIN_THUMB = 64;

/** `target` is the element that scrolls; `w` is how wide the rail is drawn. */
export function ScrollRail({ target, w = 24, testid }: { target: RefObject<HTMLElement | null>; w?: number; testid?: string }) {
  const rail = useRef<HTMLDivElement>(null);
  const [bar, setBar] = useState<{ top: number; h: number } | null>(null);
  const drag = useRef<{ y: number; top: number } | null>(null);

  useEffect(() => {
    const el = target.current; if (!el) return;
    const read = () => {
      const { scrollHeight: sh, clientHeight: ch, scrollTop: st } = el;
      const rh = rail.current?.clientHeight ?? ch;
      if (sh <= ch + 1 || rh <= MIN_THUMB) { setBar(null); return; }
      /* the capsule is as long a share of the rail as the view is of the list — the ordinary scrollbar promise:
         its length says how much of the list you are looking at */
      const h = Math.max(MIN_THUMB, Math.round((ch / sh) * rh));
      setBar({ h, top: (st / (sh - ch)) * (rh - h) });
    };
    read();
    el.addEventListener('scroll', read, { passive: true });
    const ro = new ResizeObserver(read); ro.observe(el);
    const mo = new MutationObserver(read); mo.observe(el, { childList: true, subtree: true });
    return () => { el.removeEventListener('scroll', read); ro.disconnect(); mo.disconnect(); };
  }, [target.current]);

  const scale = () => { const r = rail.current!.getBoundingClientRect(); return r.height / (rail.current!.offsetHeight || 1); };
  const scrollTo = (t: number) => {
    const el = target.current; const rh = rail.current?.clientHeight ?? 0; if (!el || !bar) return;
    const travel = rh - bar.h; if (travel <= 0) return;
    el.scrollTop = (Math.max(0, Math.min(travel, t)) / travel) * (el.scrollHeight - el.clientHeight);
  };
  /* dragging the rail scrolls the list — on a desktop that is the natural gesture, and the bar is the only hint
     that the list continues. Real pointer capture, so the drag survives the cursor leaving a 24-unit rail. */
  const down = (e: PointerEvent) => {
    const r = rail.current; if (!r || !bar) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const y = (e.clientY - r.getBoundingClientRect().top) / scale();
    if (y >= bar.top && y <= bar.top + bar.h) drag.current = { y, top: bar.top };
    else { drag.current = { y, top: y - bar.h / 2 }; scrollTo(y - bar.h / 2); }
    e.preventDefault();
  };
  const move = (e: PointerEvent) => {
    const r = rail.current; const from = drag.current; if (!r || !from) return;
    scrollTo(from.top + ((e.clientY - r.getBoundingClientRect().top) / scale() - from.y));
    e.preventDefault();
  };
  const up = (e: PointerEvent) => { drag.current = null; (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId); };

  /* the rail itself is always there — the track is the groove the list runs in; only the capsule comes and goes */
  return (
    <div class="snw-rail" ref={rail} data-testid={testid ?? 'scrollbar'} style={{ width: w }}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      <span class="snw-rail-track" />
      {bar && <span class="snw-rail-thumb" style={{ top: bar.top, height: bar.h }} />}
    </div>
  );
}
